import React, { useState } from 'react';
import { Calendar, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { LoadingSpinner } from './LoadingSpinner';
import { DateTimePicker } from './DateTimePicker';
import { CARE_TYPES } from '../constants';
import { trackInitiateCheckout, trackLead, trackBookingStarted, trackBookingCompleted } from '../utils/analytics';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: number;
  providerName: string;
  onSubmit: (bookingData: {
    providerId: number;
    appointmentDate: string;
    serviceType: string;
    notes: string;
  }) => Promise<void>;
}

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  providerId,
  providerName,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTrackedInitiate, setHasTrackedInitiate] = useState(false);
  const [formData, setFormData] = useState({
    appointmentDate: '',
    serviceType: CARE_TYPES[0],
    notes: '',
  });

  React.useEffect(() => {
    if (isOpen && !hasTrackedInitiate) {
      trackInitiateCheckout({
        content_name: providerName,
        content_category: formData.serviceType,
      });
      trackBookingStarted({
        provider_id: String(providerId),
        provider_name: providerName,
        service_type: formData.serviceType,
      });
      setHasTrackedInitiate(true);
    }
  }, [isOpen, hasTrackedInitiate, providerName, formData.serviceType, providerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.appointmentDate) {
      setError('Please select an appointment date and time');
      return;
    }

    const appointmentDateObj = new Date(formData.appointmentDate);
    if (appointmentDateObj < new Date()) {
      setError('Appointment date must be in the future');
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        providerId,
        appointmentDate: new Date(formData.appointmentDate).toISOString(),
        serviceType: formData.serviceType,
        notes: formData.notes,
      };

      console.log('Creating booking with data:', bookingData);

      await onSubmit(bookingData);

      trackLead({
        content_name: `Booking Created: ${providerName}`,
        content_category: formData.serviceType,
      });

      trackBookingCompleted({
        provider_id: String(providerId),
        provider_name: providerName,
        service_type: formData.serviceType,
        appointment_date: new Date(formData.appointmentDate).toISOString(),
        currency: 'GBP',
      });

      setFormData({
        appointmentDate: '',
        serviceType: CARE_TYPES[0],
        notes: '',
      });

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Book Service with ${providerName}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Appointment Details
          </h3>

          <div className="space-y-4">
            <DateTimePicker
              value={formData.appointmentDate}
              onChange={(value) => handleChange('appointmentDate', value)}
              label="Appointment Date & Time"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.serviceType}
                onChange={(e) => handleChange('serviceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {CARE_TYPES.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any special requirements or additional information..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                Creating Booking...
              </span>
            ) : (
              'Confirm Booking'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
